'use client'

import Loader from '@/components/common/loader'
import {
  CarClassBadge,
  getCarClassAccentClass,
} from '@/components/results/car-class-badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getOrderedChampionshipCarClasses } from '@/lib/championship-car-class-order'
import {
  getClassRelativeTiming,
  getOverallRelativeTiming,
} from '@/lib/championship-class-scoring'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'
import {
  buildStandingsCarClassGroups,
  getDefaultExpandedCarClassIds,
  getStandingsClassTimingLeader,
  UNCLASSIFIED_CAR_CLASS_ID,
} from './standings-car-class-groups'
import StandingsDriverTableRow from './standings-driver-table-row'

export default function StandingsDriverTable({
  championship,
  isLoading,
  results,
  session,
  splitId,
  selectedUserId,
  driverDisplayIdentifier,
  selectedCarClassId = 'all',
  includeUnclassified = false,
}) {
  const splitResults = useMemo(() => {
    if (!results) return []

    const filteredResults = splitId
      ? results.filter((result) => result.split?.id === splitId)
      : results

    if (!session) return filteredResults

    return filteredResults.slice().sort((a, b) => {
      const positionDifference =
        Number(a.racePosition ?? Number.MAX_SAFE_INTEGER) -
        Number(b.racePosition ?? Number.MAX_SAFE_INTEGER)

      return positionDifference || a.user.id.localeCompare(b.user.id)
    })
  }, [results, session, splitId])

  const showTeams = useMemo(
    () =>
      championship.teams.length > 0 &&
      splitResults.some(
        (result) =>
          result.currentTeam ||
          (result.previousTeams?.length ?? 0) > 0 ||
          result.team,
      ),
    [championship.teams.length, splitResults],
  )

  const showSplits = useMemo(
    () => championship.splits.length > 0 && splitResults.some((r) => r.split),
    [championship.splits.length, splitResults],
  )

  const showLapCount = useMemo(
    () =>
      !['race', 'qualifying'].includes(session?.type) &&
      splitResults.some((result) => result.laps && result.time),
    [session?.type, splitResults],
  )

  const configuredCarClasses = useMemo(
    () => getOrderedChampionshipCarClasses(championship.carClasses),
    [championship.carClasses],
  )
  const hasMultipleCarClasses = configuredCarClasses.length > 1
  const showCarClasses = useMemo(
    () =>
      hasMultipleCarClasses && splitResults.some((result) => result.carClass),
    [hasMultipleCarClasses, splitResults],
  )
  const classGroups = useMemo(() => {
    if (!hasMultipleCarClasses) return []

    return buildStandingsCarClassGroups({
      results: splitResults,
      configuredCarClasses,
      includeUnclassified,
    })
  }, [
    configuredCarClasses,
    hasMultipleCarClasses,
    includeUnclassified,
    splitResults,
  ])
  const overallLeader = useMemo(
    () =>
      session
        ? splitResults.find((result) => result.status === 'finished')
        : null,
    [session, splitResults],
  )

  const selectedClassGroup = useMemo(() => {
    if (selectedCarClassId === 'all') return null

    return (
      classGroups.find((group) => group.id === selectedCarClassId) ?? {
        id: selectedCarClassId,
        carClass: configuredCarClasses.find(
          (carClass) => carClass.id === selectedCarClassId,
        ),
        results: [],
      }
    )
  }, [classGroups, configuredCarClasses, selectedCarClassId])

  if (hasMultipleCarClasses) {
    const visibleClassGroups = selectedClassGroup
      ? [selectedClassGroup]
      : classGroups

    return (
      <CarClassResultsAccordions
        championship={championship}
        groups={visibleClassGroups}
        isLoading={isLoading}
        session={session}
        selectedUserId={selectedUserId}
        driverDisplayIdentifier={driverDisplayIdentifier}
        showTeams={showTeams}
        showSplits={showSplits}
        showLapCount={showLapCount}
        overallLeader={overallLeader}
        showManagementWarning={includeUnclassified}
      />
    )
  }

  return (
    <ResultsTable
      championship={championship}
      results={splitResults}
      isLoading={isLoading}
      session={session}
      selectedUserId={selectedUserId}
      driverDisplayIdentifier={driverDisplayIdentifier}
      showTeams={showTeams}
      showSplits={showSplits}
      showCarClasses={showCarClasses}
      showClassPosition={Boolean(session && showCarClasses)}
      showLapCount={showLapCount}
      overallLeader={overallLeader}
    />
  )
}

function CarClassResultsAccordions({
  championship,
  groups,
  isLoading,
  session,
  selectedUserId,
  driverDisplayIdentifier,
  showTeams,
  showSplits,
  showLapCount,
  overallLeader,
  showManagementWarning,
}) {
  const defaultExpandedGroupIds = getDefaultExpandedCarClassIds(groups)
  const groupPopulationKey = groups
    .map(
      (group) =>
        `${group.id}:${group.results.length > 0 ? 'populated' : 'empty'}`,
    )
    .join(':')

  return (
    <Accordion
      key={groupPopulationKey}
      type="multiple"
      defaultValue={defaultExpandedGroupIds}
      className="flex flex-col gap-4"
    >
      {groups.map((group) => {
        const isUnclassified = group.id === UNCLASSIFIED_CAR_CLASS_ID

        return (
          <AccordionItem
            key={group.id}
            value={group.id}
            className={cn(
              'rounded-sm border-0 border-l-2 px-3 transition-colors',
              getCarClassAccentClass(group.carClass?.id),
              group.results.length === 0 && 'bg-transparent opacity-60',
            )}
          >
            <AccordionTrigger
              className="focus-visible:outline-hidden px-1 py-4 focus-visible:ring-0"
              iconClassName="size-5"
            >
              <div className="flex items-center gap-3">
                <CarClassBadge
                  carClass={group.carClass}
                  className="px-4 py-2 text-sm"
                />
                <span className="text-sm font-normal text-neutral-blue-400">
                  {group.results.length === 0
                    ? 'No drivers'
                    : `${group.results.length} ${group.results.length === 1 ? 'driver' : 'drivers'}`}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              {isUnclassified && showManagementWarning ? (
                <Alert className="mb-4 border-amber-400/40 bg-amber-400/10 text-amber-100">
                  <AlertTitle>Management only</AlertTitle>
                  <AlertDescription>
                    These unclassified results are only visible to championship
                    management and site administrators.
                  </AlertDescription>
                </Alert>
              ) : null}

              <ResultsTable
                championship={championship}
                results={group.results}
                isLoading={isLoading}
                session={session}
                selectedUserId={selectedUserId}
                driverDisplayIdentifier={driverDisplayIdentifier}
                showTeams={showTeams}
                showSplits={showSplits}
                showCarClasses={false}
                showClassPosition={false}
                showLapCount={showLapCount}
                classLeader={getStandingsClassTimingLeader(group.results)}
                overallLeader={overallLeader}
              />
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}

function ResultsTable({
  results,
  isLoading,
  session,
  selectedUserId,
  driverDisplayIdentifier,
  showTeams,
  showSplits,
  showCarClasses,
  showClassPosition,
  showLapCount,
  classLeader,
  overallLeader,
}) {
  const showPoints =
    (session &&
      (session.pointDistribution.length > 0 ||
        session.metadata?.splitPointDistributions)) ||
    !session
  const hasUnclaimedResults = results.some((result) => result.user?.isUnclaimed)

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow isHeader>
            <TableHead className="hidden w-[90px] md:table-cell">
              {classLeader ? 'Class Pos.' : 'Overall Pos.'}
            </TableHead>
            <TableHead className="hidden md:table-cell">User</TableHead>
            {showTeams && (
              <TableHead className="hidden md:table-cell">Team</TableHead>
            )}
            {showSplits && (
              <TableHead className="hidden md:table-cell">
                Tier / Split
              </TableHead>
            )}
            {showCarClasses && (
              <TableHead className="hidden min-w-[150px] md:table-cell">
                Car Class
              </TableHead>
            )}
            {showClassPosition && (
              <TableHead className="hidden w-[100px] md:table-cell">
                Class Pos.
              </TableHead>
            )}
            {showLapCount && (
              <TableHead className="hidden w-[100px] md:table-cell">
                Laps
              </TableHead>
            )}
            {session && (
              <TableHead className="hidden w-[140px] md:table-cell">
                Time
              </TableHead>
            )}
            {showPoints && (
              <TableHead className="hidden w-[68px] md:table-cell">
                Pts
              </TableHead>
            )}
          </TableRow>
        </TableHeader>

        <TableBody className="overflow-x-auto tabular-nums">
          {isLoading ? (
            <TableRow isLoading>
              <TableCell colSpan={11}>
                <Loader size="lg" />
              </TableCell>
            </TableRow>
          ) : results.length > 0 ? (
            results.map((result, index) => {
              const isOverallLeader =
                overallLeader &&
                (result === overallLeader ||
                  (overallLeader.user?.id &&
                    result.user?.id === overallLeader.user.id))
              const timingOverride = classLeader
                ? getClassRelativeTiming(result, classLeader, overallLeader)
                : isOverallLeader
                  ? { displayValue: 'Leader' }
                  : !result.carClass && overallLeader
                    ? getOverallRelativeTiming(result, overallLeader, {
                        sessionType: session?.type,
                      })
                    : null
              const displayedPosition = classLeader
                ? result.classPosition || index + 1
                : result.racePosition || index + 1

              return (
                <StandingsDriverTableRow
                  key={result.user.id}
                  rowIndex={displayedPosition - 1}
                  result={result}
                  session={session}
                  showTeams={showTeams}
                  showSplits={showSplits}
                  showCarClasses={showCarClasses}
                  showClassPosition={showClassPosition}
                  showLapCount={showLapCount}
                  isSelected={selectedUserId === result.user.id}
                  driverDisplayIdentifier={driverDisplayIdentifier}
                  timingOverride={timingOverride}
                />
              )
            })
          ) : (
            <TableRow>
              <TableCell className="text-center" colSpan={11}>
                No results published.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {hasUnclaimedResults && (
        <p className="text-sm text-neutral-blue-500">
          * Unassigned to a Grid Finder user.
        </p>
      )}
    </div>
  )
}
